import en from '../i18n/en.js';
import { DEFAULT_LOCALE, getLocale, isSupportedLocale } from '../i18n/locales.js';

/**
 * Dictionaries are code-split: only English (the default and fallback) ships
 * with the entry bundle, every other locale is fetched when it is selected or
 * detected. The active locale comes from the URL, not from storage, so a
 * shared `/de/games/` link always renders German.
 */
const DICTIONARIES = { en };
const LOADERS = {
  de: () => import('../i18n/de.js'),
  fr: () => import('../i18n/fr.js'),
  'es-ES': () => import('../i18n/es-ES.js'),
  'es-419': () => import('../i18n/es-419.js'),
  'pt-PT': () => import('../i18n/pt-PT.js'),
  'pt-BR': () => import('../i18n/pt-BR.js'),
  ar: () => import('../i18n/ar.js'),
  ru: () => import('../i18n/ru.js'),
  uk: () => import('../i18n/uk.js'),
  pl: () => import('../i18n/pl.js'),
  cs: () => import('../i18n/cs.js'),
  sk: () => import('../i18n/sk.js'),
  sl: () => import('../i18n/sl.js'),
  hu: () => import('../i18n/hu.js'),
  da: () => import('../i18n/da.js'),
  fi: () => import('../i18n/fi.js'),
  sv: () => import('../i18n/sv.js'),
  no: () => import('../i18n/no.js'),
  ko: () => import('../i18n/ko.js'),
  zh: () => import('../i18n/zh.js'),
};

const FALLBACK = DEFAULT_LOCALE;

let current = FALLBACK;

const warnOnParity = (code, dictionary) => {
  const missing = Object.keys(en).filter((key) => !(key in dictionary));
  const extra = Object.keys(dictionary).filter((key) => !(key in en));
  if (missing.length || extra.length) {
    console.warn(`i18n out of sync: ${code}`, { missing, extra });
  }
};

const load = async (code) => {
  if (DICTIONARIES[code]) return DICTIONARIES[code];
  const loader = LOADERS[code];
  if (!loader) throw new Error(`No dictionary loader for locale "${code}"`);
  const module = await loader();
  DICTIONARIES[code] = module.default;
  if (typeof __DEV__ !== 'undefined' && __DEV__) warnOnParity(code, module.default);
  return DICTIONARIES[code];
};

const applyDocumentLanguage = () => {
  const locale = getLocale(current);
  document.documentElement.lang = locale.htmlLang;
  document.documentElement.dir = locale.dir;
};

/**
 * Look up a key. Missing keys fall back to English and then to the key itself,
 * which makes them loud in development instead of rendering as blanks.
 * `{name}` placeholders are replaced from `params`.
 */
export const t = (key, params) => {
  const dictionary = DICTIONARIES[current] ?? DICTIONARIES[FALLBACK];
  const value = dictionary[key] ?? DICTIONARIES[FALLBACK][key] ?? key;
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (match, name) => (name in params ? params[name] : match));
};

export const getLanguage = () => current;

/**
 * Set the dictionary used while the static pages are rendered at build time.
 * Runtime code goes through `initI18n()` instead, which also touches <html>.
 */
export const setRenderLocale = (code, dictionary) => {
  if (!isSupportedLocale(code)) throw new Error(`Unsupported locale "${code}"`);
  if (!dictionary || typeof dictionary !== 'object') {
    throw new Error(`Missing dictionary for locale "${code}"`);
  }
  DICTIONARIES[code] = dictionary;
  current = code;
};

export const initI18n = async (code = FALLBACK) => {
  current = isSupportedLocale(code) ? code : FALLBACK;
  try {
    await load(current);
  } catch (error) {
    console.error(`Could not load locale "${current}", falling back to ${FALLBACK}.`, error);
    current = FALLBACK;
  }
  applyDocumentLanguage();
};
